import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate, NavLink } from 'react-router-dom';
import {
  FiCalendar,
  FiCheckCircle,
  FiX,
  FiRefreshCw,
  FiImage,
  FiBook,
} from 'react-icons/fi';
import Sidebar from '../components/Sidebar';
import DashboardHeader from '../components/DashboardHeader';
import { useSidebar } from '../contexts/SidebarContext';
import { authAPI, courseAPI, OnlineCourse, OnlineCourseType, getOnlineCourseImageURL } from '../services/api';
import '../components/Dashboard.css';
import './PageTemplate.css';
import './Booking.css';

interface OnlineCoursesProps {
  onLogout?: () => void;
  userEmail?: string;
}

const ROUTE_TO_API: Record<string, OnlineCourseType> = {
  private: 'private',
  workshop: 'workshop',
  'vip-feedback': 'vip_feedback',
};

const TABS: { path: string; label: string; api: OnlineCourseType }[] = [
  { path: '/online-courses/private', label: 'Private', api: 'private' },
  { path: '/online-courses/workshop', label: 'Workshop', api: 'workshop' },
  { path: '/online-courses/vip-feedback', label: 'VIP Feedback', api: 'vip_feedback' },
];

const OnlineCourses: React.FC<OnlineCoursesProps> = ({ onLogout, userEmail }) => {
  const { type } = useParams();
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const [userInfo, setUserInfo] = useState({
    email: userEmail || 'anonymous.user@naati.edu',
    organization: 'NAATI',
    first_name: '',
  });
  const [courses, setCourses] = useState<OnlineCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<OnlineCourse | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [enrollForm, setEnrollForm] = useState({
    applicant_message: '',
    contact_phone: '',
    contact_address: '',
  });

  const apiType: OnlineCourseType | null = type ? ROUTE_TO_API[type] ?? null : null;

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await authAPI.getProfile();
        setUserInfo({
          email: profile.email || userEmail || 'anonymous.user@naati.edu',
          organization: profile.organization || 'NAATI',
          first_name: profile.first_name || '',
        });
      } catch {
        // keep defaults
      }
    };
    loadProfile();
  }, [userEmail]);

  useEffect(() => {
    if (!type || !ROUTE_TO_API[type]) {
      navigate('/online-courses/private', { replace: true });
    }
  }, [type, navigate]);

  const loadCourses = useCallback(async () => {
    if (!apiType) return;
    try {
      setLoading(true);
      setError(null);
      const data = await courseAPI.listCourses(apiType);
      setCourses(data);
    } catch (err: unknown) {
      setError((err as Error).message || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  }, [apiType]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  const getTitle = () => {
    if (type === 'vip-feedback') return 'VIP Feedback';
    if (type === 'private') return 'Private Courses';
    if (type === 'workshop') return 'Workshop';
    return 'Online Courses';
  };

  const openEnroll = (course: OnlineCourse) => {
    setSelectedCourse(course);
    setEnrollForm({ applicant_message: '', contact_phone: '', contact_address: '' });
    setShowEnrollModal(true);
  };

  const submitEnroll = async () => {
    if (!selectedCourse) return;
    if (!enrollForm.contact_address.trim()) {
      alert('Please enter your contact address or how we can reach you.');
      return;
    }
    try {
      setSubmitting(true);
      await courseAPI.createEnrollment({
        course: selectedCourse.id,
        applicant_message: enrollForm.applicant_message.trim() || undefined,
        contact_phone: enrollForm.contact_phone.trim() || undefined,
        contact_address: enrollForm.contact_address.trim(),
      });
      setShowEnrollModal(false);
      setSelectedCourse(null);
      setShowSuccess(true);
      loadCourses();
    } catch (err: unknown) {
      alert((err as Error).message || 'Enrollment failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!apiType) {
    return null;
  }

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar />
      <div className="dashboard-main">
        <DashboardHeader
          userEmail={userInfo.email}
          organization={userInfo.organization}
          onLogout={onLogout}
        />
        <div className="dashboard-content">
          <div className="booking-page">
            <motion.div
              className="page-header"
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <h1 className="page-title">{getTitle()}</h1>
              <p className="page-description">
                Browse courses and submit an enrollment request. Your account email is on file; add details and
                contact information below.
              </p>
            </motion.div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: '1.5rem' }}>
              {TABS.map((t) => (
                <NavLink
                  key={t.path}
                  to={t.path}
                  style={({ isActive }) => ({
                    textDecoration: 'none',
                    border: isActive ? '1px solid #E63946' : '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 8,
                    padding: '0.5rem 1rem',
                    color: '#fff',
                    background: isActive ? 'rgba(230,57,70,0.25)' : 'rgba(255,255,255,0.06)',
                    fontWeight: isActive ? 600 : 400,
                  })}
                >
                  {t.label}
                </NavLink>
              ))}
            </div>

            {error && (
              <div className="error-message">
                {error}
                <button type="button" className="retry-btn" onClick={loadCourses}>
                  {React.createElement(FiRefreshCw as React.ComponentType<{ size?: number }>, { size: 16 })}
                  <span> Retry</span>
                </button>
              </div>
            )}

            {loading ? (
              <div className="loading-container">
                {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, {
                  className: 'spinner',
                  size: 32,
                })}
                <p>Loading courses...</p>
              </div>
            ) : courses.length === 0 ? (
              <div className="empty-state">
                {React.createElement(FiBook as React.ComponentType<{ size?: number }>, { size: 48 })}
                <p>No courses available in this category yet.</p>
              </div>
            ) : (
              <div className="services-grid">
                {courses.map((course) => (
                  <motion.div
                    key={course.id}
                    className="service-card"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    whileHover={{ y: -4 }}
                  >
                    <div className="service-image">
                      {course.image_garage_key ? (
                        <img src={getOnlineCourseImageURL(course.id)} alt={course.title} />
                      ) : (
                        <div className="service-image-placeholder">
                          {React.createElement(FiImage as React.ComponentType<{ size?: number }>, { size: 48 })}
                        </div>
                      )}
                    </div>
                    <div className="service-info">
                      <h3>{course.title}</h3>
                      <p className="service-description">{course.description || '—'}</p>
                      {course.has_user_enrollment ? (
                        <div className="request-sent-message">
                          {React.createElement(FiCheckCircle as React.ComponentType<{ size?: number }>, { size: 18 })}
                          <span>Enrollment submitted or approved</span>
                        </div>
                      ) : (
                        <button type="button" className="btn-book" onClick={() => openEnroll(course)}>
                          {React.createElement(FiCalendar as React.ComponentType<{ size?: number }>, { size: 18 })}
                          <span>Enroll</span>
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showEnrollModal && selectedCourse && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !submitting && setShowEnrollModal(false)}
          >
            <motion.div
              className="modal-content booking-request-modal"
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Enroll: {selectedCourse.title}</h2>
                <button type="button" className="modal-close" disabled={submitting} onClick={() => setShowEnrollModal(false)}>
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 24 })}
                </button>
              </div>
              <div className="modal-body">
                <p style={{ color: '#aaa', fontSize: 14, marginBottom: 12 }}>
                  Account email: <strong style={{ color: '#fff' }}>{userInfo.email}</strong>
                </p>
                <div className="form-group">
                  <label>Additional message (optional)</label>
                  <textarea
                    rows={3}
                    value={enrollForm.applicant_message}
                    onChange={(e) => setEnrollForm({ ...enrollForm, applicant_message: e.target.value })}
                    disabled={submitting}
                    placeholder="Goals, background, preferred schedule notes..."
                  />
                </div>
                <div className="form-group">
                  <label>Phone (optional)</label>
                  <input
                    type="tel"
                    value={enrollForm.contact_phone}
                    onChange={(e) => setEnrollForm({ ...enrollForm, contact_phone: e.target.value })}
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label>Contact address / details *</label>
                  <textarea
                    rows={3}
                    required
                    value={enrollForm.contact_address}
                    onChange={(e) => setEnrollForm({ ...enrollForm, contact_address: e.target.value })}
                    disabled={submitting}
                    placeholder="Address, suburb, or how you prefer to be contacted beyond email"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" disabled={submitting} onClick={() => setShowEnrollModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn-primary" disabled={submitting} onClick={submitEnroll}>
                  {submitting ? 'Submitting...' : 'Submit enrollment'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSuccess && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowSuccess(false)}
          >
            <motion.div
              className="modal-content success-modal"
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="success-icon">
                {React.createElement(FiCheckCircle as React.ComponentType<{ size?: number }>, { size: 56 })}
              </div>
              <h2>Request received</h2>
              <p style={{ color: '#aaa', textAlign: 'center' }}>
                Your enrollment request is pending review. We will contact you using your account email and the details
                you provided.
              </p>
              <button type="button" className="btn-primary" style={{ marginTop: 16 }} onClick={() => setShowSuccess(false)}>
                OK
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OnlineCourses;
